import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { getAnnotationColor } from "../../annotationColors";

const TOP_N = 15;

// Standard amino acid HET codes — excluded because they are protein residues,
// not ligands. Modified residues (e.g. TPO, SEP, PTR) are kept.
const STANDARD_AMINO_ACIDS = new Set([
  "ALA", "ARG", "ASN", "ASP", "CYS",
  "GLN", "GLU", "GLY", "HIS", "ILE",
  "LEU", "LYS", "MET", "PHE", "PRO",
  "SER", "THR", "TRP", "TYR", "VAL",
]);

// Other non-ligand HET codes commonly found in PDB structures.
// Extend as needed.
const OTHER_EXCLUDED = new Set([
  "HOH",          // water
  // "MSE",       // selenomethionine — uncomment to exclude
]);

const isExcluded = (het) =>
  STANDARD_AMINO_ACIDS.has(het) || OTHER_EXCLUDED.has(het);

const TopLigandsChart = ({ ligandData }) => {
  const [plotData, setPlotData] = useState([]);
  const [layout, setLayout] = useState({});

  useEffect(() => {
    if (!Array.isArray(ligandData) || ligandData.length === 0) {
      setPlotData([]);
      return;
    }

    // Count distinct PDB structures each ligand (HET code) appears in
    const ligandToPDBs = new Map();
    const ligandAnnotations = new Map();

    ligandData.forEach((row) => {
      const het = (row?.ligand || "").trim().toUpperCase();
      const pdb = (row?.pdbId || "").trim().toUpperCase();
      const annotation = (row?.annotation || "unknown").trim();
      if (!het || !pdb) return;

      // Skip standard amino acids and other non-ligand residues
      if (isExcluded(het)) return;

      if (!ligandToPDBs.has(het)) ligandToPDBs.set(het, new Set());
      ligandToPDBs.get(het).add(pdb);

      if (!ligandAnnotations.has(het)) ligandAnnotations.set(het, annotation);
    });

    const ranked = Array.from(ligandToPDBs.entries())
      .map(([het, pdbs]) => ({
        het,
        count: pdbs.size,
        annotation: ligandAnnotations.get(het) || "unknown",
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, TOP_N);

    if (ranked.length === 0) {
      setPlotData([]);
      return;
    }

    // Reverse for horizontal bar (most frequent on top)
    const ordered = [...ranked].reverse();
    const yLabels = ordered.map((r) => r.het);

    // One trace per annotation present in the top ranking
    const annotationsPresent = [...new Set(ordered.map((r) => r.annotation))];

    const traces = annotationsPresent.map((annotation) => {
      const xValues = ordered.map((r) =>
        r.annotation === annotation ? r.count : null
      );
      return {
        x: xValues,
        y: yLabels,
        name: annotation,
        type: "bar",
        orientation: "h",
        hovertemplate:
          `Ligand: %{y}<br>Structures: %{x}<br>Type: ${annotation}<extra></extra>`,
        marker: {
          color: getAnnotationColor(annotation),
          line: { color: "#333", width: 0.5 },
        },
      };
    });

    setPlotData(traces);

    setLayout({
      autosize: true,
      font: { size: 9 },
      title: { text: `Top ${ranked.length} Ligands by Frequency` },
      xaxis: {
        title: { text: "# of structures" },
      },
      yaxis: {
        type: "category",
        title: { text: "Ligand (HET code)" },
        automargin: true,
      },
      barmode: "overlay",
      showlegend: true,
      legend: {
        title: { text: "Annotation" },
        orientation: "v",
        x: 1.02,
        y: 1,
      },
      margin: { t: 50, b: 60, l: 80, r: 80 },
    });
  }, [ligandData]);

  return (
    <div className="aspect-square card">
      <Plot
        data={plotData}
        layout={layout}
        useResizeHandler
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
};

export default TopLigandsChart;